import { memo, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

const Component = () => {
  const [t] = useTranslation(['backend', 'common', 'messages']);

  // Returning `undefined` leaves the issue to Zod's default message.
  // eslint-disable-next-line no-restricted-syntax
  const getErrorMessage = useCallback<z.ZodErrorMap>(
    (issue) => {
      const isMissing = issue.input === undefined || issue.input === null;

      if (issue.code === 'invalid_type') {
        if (isMissing) {
          return t('messages:FIELD_IS_REQUIRED');
        }

        if (issue.expected === 'number') {
          return t('messages:SHOULD_BE_NUMBER');
        }
      }

      // An enum or a literal reports a missing value as `invalid_value`, not as `invalid_type`.
      if (issue.code === 'invalid_value' && isMissing) {
        return t('messages:FIELD_IS_REQUIRED');
      }

      if (issue.code === 'too_small') {
        if (issue.origin === 'number') {
          return issue.inclusive
            ? t('messages:LIMIT_EXCEEDED_MIN', { min: issue.minimum })
            : t('messages:LIMITED_MIN', { min: issue.minimum });
        }

        if (issue.origin === 'string') {
          if (issue.minimum === 1) {
            return t('messages:FIELD_IS_REQUIRED');
          }

          return t('messages:VALUE_LENGTH_EXCEEDED_MIN', { min: issue.minimum });
        }

        if (issue.origin === 'array' && issue.minimum === 1) {
          return t('messages:AT_LEAST_ONE_ITEM_REQUIRED');
        }
      }

      if (issue.code === 'too_big') {
        if (issue.origin === 'number') {
          return issue.inclusive
            ? t('messages:LIMIT_EXCEEDED_MAX', { max: issue.maximum })
            : t('messages:LIMITED_MAX', { max: issue.maximum });
        }

        if (issue.origin === 'string' && issue.inclusive) {
          return t('messages:VALUE_LENGTH_EXCEEDED_MAX', { max: issue.maximum });
        }
      }

      if (issue.code === 'invalid_format' && issue.format === 'email') {
        return t('messages:INVALID_EMAIL');
      }

      return undefined;
    },
    [t],
  );

  useEffect(() => {
    z.config({ customError: getErrorMessage });
  }, [getErrorMessage]);

  return null;
};

const ErrorMessagesConfigurator = memo(Component);

export { ErrorMessagesConfigurator };
